import React, { useState } from "react";
import axios from "axios";
import { Loader2, Send, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { BRAND, telHref, waHref, SHIFTING_TYPES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  mobile: "",
  pickup: "",
  drop: "",
  shifting_type: SHIFTING_TYPES[0],
  message: "",
};

export default function Quote() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Apna naam likhiye";
    if (!/^[0-9+\-\s]{7,15}$/.test(form.mobile.trim())) return "Sahi mobile number dijiye";
    if (form.pickup.trim().length < 2) return "Pickup location likhiye";
    if (form.drop.trim().length < 2) return "Drop location likhiye";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);

    // Pre-open window synchronously inside the user gesture so popup blockers don't kill it.
    const waWindow = window.open("", "_blank");

    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        pickup: form.pickup.trim(),
        drop: form.drop.trim(),
        shifting_type: form.shifting_type,
        message: form.message.trim() || null,
      });
      toast.success("Aapki request mil gayi! Hum jaldi call karenge. Redirecting to WhatsApp...");

      // Build WhatsApp prefilled message
      const wMsg = [
        "Hi A Seven Packers and Movers,",
        `I would like a free quote.`,
        ``,
        `Name: ${form.name}`,
        `Mobile: ${form.mobile}`,
        `Pickup: ${form.pickup}`,
        `Drop: ${form.drop}`,
        `Type: ${form.shifting_type}`,
        form.message ? `Message: ${form.message}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      const url = waHref(wMsg);
      if (waWindow && !waWindow.closed) {
        waWindow.location.href = url;
      } else {
        // Fallback if the pre-open was blocked anyway
        window.location.href = url;
      }
      setForm(initial);
    } catch (err) {
      if (waWindow && !waWindow.closed) waWindow.close();
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Bahut zyada requests. Thodi der baad try kijiye.");
      } else if (status === 422) {
        toast.error("Form me kuch galat hai. Fields check kijiye.");
      } else {
        toast.error("Quote submit nahi ho saka. Phone par try kijiye.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-16 md:py-24 bg-gradient-to-br from-blue-800 via-blue-900 to-slate-900 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center relative">
        <div className="text-white">
          <span className="inline-block px-3 py-1 rounded-full bg-orange-500 text-white text-xs font-bold uppercase tracking-wider">
            Get Free Quote
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Apne shifting ka <span className="text-yellow-400">free quote</span> abhi paayein
          </h2>
          <p className="mt-5 text-blue-100 leading-relaxed text-lg">
            Form bhariye, hum aapko quickly call karenge aur kaam dekh kar best price denge. Koi
            hidden charges nahi. WhatsApp par bhi message kar sakte hain.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={telHref}
              data-testid="quote-call-btn"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-blue-900 font-bold shadow-lg hover:shadow-2xl hover:scale-[1.02] transition"
            >
              <Phone size={18} /> Call {BRAND.phone}
            </a>
            <a
              href={waHref()}
              target="_blank"
              rel="noreferrer"
              data-testid="quote-whatsapp-btn"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold shadow-lg hover:shadow-2xl hover:scale-[1.02] transition"
            >
              <MessageCircle size={18} /> WhatsApp Now
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center border border-white/15">
              <div className="text-2xl font-extrabold text-yellow-400">1000+</div>
              <div className="text-xs text-blue-100 mt-0.5">Happy Customers</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center border border-white/15">
              <div className="text-2xl font-extrabold text-orange-400">24/7</div>
              <div className="text-xs text-blue-100 mt-0.5">Support</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center border border-white/15">
              <div className="text-2xl font-extrabold text-white">100%</div>
              <div className="text-xs text-blue-100 mt-0.5">Safe Shifting</div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="quote-form"
          className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 border-t-4 border-orange-500"
        >
          <h3 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Free Quote Form
          </h3>
          <p className="text-sm text-slate-500 mt-1">Form bhariye — hum 5 min me call karenge.</p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Customer Name *" testid="quote-name">
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                required
                data-testid="quote-name-input"
                className="input"
                placeholder="Aapka naam"
              />
            </Field>
            <Field label="Mobile Number *" testid="quote-mobile">
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9+\-() ]{7,20}"
                autoComplete="tel"
                value={form.mobile}
                onChange={(e) => update("mobile", e.target.value)}
                required
                data-testid="quote-mobile-input"
                className="input"
                placeholder="10-digit mobile"
              />
            </Field>
            <Field label="Pickup Location *" testid="quote-pickup">
              <input
                type="text"
                value={form.pickup}
                onChange={(e) => update("pickup", e.target.value)}
                required
                data-testid="quote-pickup-input"
                className="input"
                placeholder="From where? e.g. Uttam Nagar"
              />
            </Field>
            <Field label="Drop Location *" testid="quote-drop">
              <input
                type="text"
                value={form.drop}
                onChange={(e) => update("drop", e.target.value)}
                required
                data-testid="quote-drop-input"
                className="input"
                placeholder="To where? e.g. Noida"
              />
            </Field>
            <Field label="Type of Shifting *" testid="quote-type" className="sm:col-span-2">
              <select
                value={form.shifting_type}
                onChange={(e) => update("shifting_type", e.target.value)}
                data-testid="quote-type-input"
                className="input"
              >
                {SHIFTING_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Message" testid="quote-message" className="sm:col-span-2">
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                data-testid="quote-message-input"
                className="input resize-none"
                placeholder="Saamaan ki list, special instructions, etc."
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="quote-submit-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold shadow-lg hover:shadow-2xl transition"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
            {submitting ? "Submitting..." : "Get Free Quote"}
          </button>

          <p className="mt-3 text-[11px] text-slate-400 text-center">
            By submitting, aap hum se contact ke liye consent dete hain.
          </p>
        </form>
      </div>

      <style>{`
        .input { width:100%; padding: 0.7rem 0.9rem; border-radius: 0.6rem; border: 1px solid #CBD5E1; outline: none; font-size: 0.9rem; background: #fff; }
        .input:focus { border-color: #1E40AF; box-shadow: 0 0 0 3px rgba(30,64,175,0.18); }
      `}</style>
    </section>
  );
}

function Field({ label, children, className = "", testid }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
